import React, { useEffect, useState } from "react";
import { Box, Button, Chip, IconButton, Typography } from "@mui/joy";
import CloseIcon from "@mui/icons-material/Close";
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import CustomModal from "components/common/CustomModal";
import { theme } from "styles/theme";
import {
  GuideDTO,
  GuideName,
  GuideVersionConflictError,
  listGuides,
  putGuide,
  resetGuide,
} from "api/taskGuidesApi";

interface GuideEditorModalProps {
  open: boolean;
  onClose: () => void;
}

const GUIDE_ORDER: GuideName[] = ["tone", "structure"];
const GUIDE_LABELS: Record<GuideName, string> = {
  tone: "톤 (tone.md)",
  structure: "구조 (structure.md)",
};

function guideErrorText(e: unknown): string {
  return e instanceof Error ? e.message : "오류가 발생했어요";
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

export default function GuideEditorModal({
  open,
  onClose,
}: GuideEditorModalProps) {
  const [guides, setGuides] = useState<Partial<Record<GuideName, GuideDTO>>>(
    {}
  );
  const [drafts, setDrafts] = useState<Record<GuideName, string>>({
    tone: "",
    structure: "",
  });
  const [activeTab, setActiveTab] = useState<GuideName>("tone");
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<GuideName | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 열릴 때마다 최신 가이드 로드 (저장 없이 닫았다 다시 열면 서버 상태로 리셋).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listGuides()
      .then((list) => {
        if (cancelled) return;
        const byName: Partial<Record<GuideName, GuideDTO>> = {};
        const nextDrafts: Record<GuideName, string> = {
          tone: "",
          structure: "",
        };
        for (const g of list) {
          byName[g.name] = g;
          nextDrafts[g.name] = g.content;
        }
        setGuides(byName);
        setDrafts(nextDrafts);
      })
      .catch((e) => {
        if (!cancelled) setError(guideErrorText(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const applyGuide = (g: GuideDTO) => {
    setGuides((prev) => ({ ...prev, [g.name]: g }));
    setDrafts((prev) => ({ ...prev, [g.name]: g.content }));
  };

  const handleSave = async (name: GuideName) => {
    const base = guides[name];
    if (!base) return;
    setSaving(name);
    setError(null);
    try {
      applyGuide(await putGuide(name, drafts[name], base.version));
    } catch (e) {
      if (e instanceof GuideVersionConflictError) {
        // 다른 editor 가 먼저 저장 — 최신본으로 갱신하고 알린다.
        applyGuide(e.current);
        setError(
          "다른 사람이 먼저 저장했어요. 최신본을 불러왔으니 다시 확인 후 저장해주세요."
        );
      } else {
        setError(guideErrorText(e));
      }
    } finally {
      setSaving(null);
    }
  };

  const handleReset = async (name: GuideName) => {
    setSaving(name);
    setError(null);
    try {
      applyGuide(await resetGuide(name));
    } catch (e) {
      setError(guideErrorText(e));
    } finally {
      setSaving(null);
    }
  };

  const active = guides[activeTab];
  const activeDraft = drafts[activeTab] ?? "";
  const isOverride = active?.source === "override";
  const savingActive = saving === activeTab;
  const dirtyActive = active ? activeDraft !== active.content : false;
  const emptyActive = activeDraft.trim().length === 0;
  const canSave = dirtyActive && !emptyActive && !savingActive && !loading;

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <Box component="span">가이드 편집</Box>
          <IconButton
            variant="plain"
            size="sm"
            color="neutral"
            aria-label="닫기"
            onClick={onClose}
            sx={{ minHeight: "32px", minWidth: "32px", borderRadius: "8px" }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      }
      subtitle="에이전트가 참고하는 톤·구조 가이드입니다. 저장하면 다음 작성/검토 턴부터 반영됩니다."
      dialogProps={{ sx: { width: 920, maxWidth: "96vw", maxHeight: "92vh" } }}
      actions={[
        {
          label: "저장",
          variant: "Solid",
          color: "primary",
          onClick: () => handleSave(activeTab),
          buttonProps: { loading: savingActive, disabled: !canSave },
        },
      ]}
    >
      {/* 톤/구조 탭 — 워크스페이스의 편집/미리보기/코드 탭과 동일 스타일 */}
      <Box
        sx={{
          display: "flex",
          gap: "4px",
          borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
          mb: "12px",
        }}
      >
        {GUIDE_ORDER.map((name) => {
          const isActive = activeTab === name;
          return (
            <Box
              key={name}
              onClick={() => setActiveTab(name)}
              sx={{
                px: "10px",
                py: "6px",
                cursor: "pointer",
                borderBottom: `2px solid ${
                  isActive ? theme.vars.palette.Content.Strong : "transparent"
                }`,
                color: isActive
                  ? theme.vars.palette.Content.Strong
                  : theme.vars.palette.Content.Weak,
                fontWeight: isActive ? 700 : 500,
                fontSize: "12px",
                userSelect: "none",
              }}
            >
              {GUIDE_LABELS[name]}
            </Box>
          );
        })}
      </Box>

      {/* 상태 칩 + 되돌리기 */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          minHeight: "24px",
          mb: "8px",
        }}
      >
        <Chip
          size="sm"
          variant="soft"
          color={isOverride ? "primary" : "neutral"}
        >
          {isOverride ? "수정됨" : "기본값"}
        </Chip>
        {active?.updated_at && (
          <Typography
            level="body-xs"
            sx={{ color: theme.vars.palette.Content.Medium }}
          >
            마지막 저장 {formatTime(active.updated_at)}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          size="sm"
          variant="plain"
          color="neutral"
          disabled={savingActive || !isOverride}
          onClick={() => handleReset(activeTab)}
        >
          기본값으로 되돌리기
        </Button>
      </Box>

      {/* 마크다운 에디터 (편집 + 라이브 미리보기) */}
      <Box data-color-mode="light">
        <MDEditor
          value={activeDraft}
          onChange={(v) =>
            setDrafts((prev) => ({ ...prev, [activeTab]: v ?? "" }))
          }
          height={460}
          preview="live"
        />
      </Box>

      {error && (
        <Typography
          level="body-sm"
          sx={{ mt: "8px", color: theme.vars.palette.danger[500] }}
        >
          {error}
        </Typography>
      )}
    </CustomModal>
  );
}
